// ============================================================================
// SIMPLIFIED PROMPT MANAGEMENT API CLIENT
// ============================================================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// ============================================================================
// CORE INTERFACES
// ============================================================================

export interface CustomPromptRequest {
  prompt_type: "image" | "video"; // Type of analysis: "image" for static images, "video" for video/animation/3D
  project_ids: string[]; // List of project IDs as expected by backend
  custom_system_instructions?: string;
  custom_user_instructions?: string;
  custom_metadata_schema?: Record<string, any>;
  custom_system_focus_areas?: Record<string, string[]>;
  custom_user_focus_areas?: Record<string, string[]>;
  custom_focus_areas?: Record<string, any>; // Legacy field
  custom_output_format?: string;
  validation_rules?: Record<string, any>;
  llm_parameters?: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
    frequency_penalty?: number;
    presence_penalty?: number;
  };
  allowed_project_ids?: string[];
}

export interface CustomPromptResponse {
  success: boolean;
  message: string;
  version: string;
  project_id: string;
  custom_system_instructions?: string;
  custom_user_instructions?: string;
  custom_metadata_schema?: Record<string, any>;
  custom_focus_areas?: Record<string, any>;
  llm_parameters?: Record<string, any>;
}

export interface CacheStatsResponse {
  total_prompts: number;
  custom_prompts: number;
  default_prompts: number;
  supported_asset_types: string[];
  cache_hit_rate: number;
  last_updated: string;
}

export interface ProjectProcessingRequest {
  project_ids: string[];
  use_custom_prompts: boolean;
}

export interface ProjectProcessingResponse {
  project_id: string;
  status: string;
  message: string;
  assets_queued: number;
}

// ============================================================================
// PROCESSING INTERFACES (for components)
// ============================================================================

export interface ProcessingResult {
  asset_id?: string;
  project_id?: string;
  status: 'success' | 'failed' | 'processing';
  result?: {
    description?: string;
    title?: string;
    tags?: string[];
    metadata?: Record<string, any>;
  };
  error?: string;
  processing_time?: number;
  timestamp?: string;
  custom_prompt_used?: boolean;
}

export interface EvaluationMetrics {
  total_assets: number;
  processed_assets: number;
  failed_assets: number;
  success_rate: number;
  average_processing_time: number;
  custom_prompt_usage_rate: number;
  asset_type_distribution: Record<string, number>;
  error_distribution: Record<string, number>;
  token_usage?: {
    total_tokens: number;
    average_tokens_per_asset: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    efficiency_score: number;
    cost_estimate?: number;
  };
}

// ============================================================================
// CORE API FUNCTIONS
// ============================================================================

export async function customizePrompt(request: CustomPromptRequest): Promise<CustomPromptResponse> {
  const response = await fetch(`${API_BASE_URL}/prompts/customize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to customize prompt: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function getCacheStats(): Promise<CacheStatsResponse> {
  const response = await fetch(`${API_BASE_URL}/prompts/stats`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get cache stats: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function getDefaultPrompts(): Promise<{
  success: boolean;
  default_prompts: Record<string, any>;
  supported_asset_types: string[];
}> {
  const response = await fetch(`${API_BASE_URL}/prompts/defaults`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get default prompts: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function processProjectsWithCustomPrompts(
  request: ProjectProcessingRequest
): Promise<ProjectProcessingResponse[]> {
  const response = await fetch(`${API_BASE_URL}/assets/processing/enhanced/projects/batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to process projects: ${response.status} - ${errorText}`);
  }

  return response.json();
}

// ============================================================================
// HUMAN EVALUATION FUNCTIONS
// ============================================================================

export interface HumanEvaluationRequest {
  asset_id: string;
  project_id: string;
  rating: number; // 1-5 scale (stars)
  feedback?: string;
  evaluator_id?: string;
  evaluation_type?: 'quality' | 'accuracy' | 'relevance' | 'completeness';
  custom_prompt_used?: boolean;
  metadata?: Record<string, any>;
}

export interface HumanEvaluationResponse {
  success: boolean;
  message: string;
  evaluation_id: string;
  asset_id: string;
  project_id: string;
  submitted_at: string;
}

export async function submitHumanEvaluation(
  request: HumanEvaluationRequest
): Promise<HumanEvaluationResponse> {
  const response = await fetch(`${API_BASE_URL}/prompts/evaluation/human`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to submit evaluation: ${response.status} - ${errorText}`);
  }

  return response.json();
}

// ============================================================================
// PROMPT BUILD API FUNCTION
// ============================================================================

export interface PromptBuildRequest {
  prompt_type: string;
  project_id?: string;
  profile_id?: string; // Direct profile ID to use for building the prompt
  context?: Record<string, any>;
  custom_system_instructions?: string;
  custom_user_instructions?: string;
  custom_output_format?: string;
  custom_metadata_schema?: Record<string, any>;
  custom_system_focus_areas?: Record<string, string[]>;
  custom_user_focus_areas?: Record<string, string[]>;
  custom_focus_areas?: Record<string, any>;
  system_role_definition?: any;
  system_json_structure?: any;
  system_instructions?: any;
  user_instructions?: any;
  output_format?: any;
}

export interface PromptBuildResponse {
  success: boolean;
  message: string;
  system_prompt?: string;
  user_prompt?: string;
  context?: Record<string, any>;
  metadata?: Record<string, any>;
  warnings?: string[];
  custom_prompt_used?: boolean;
}

export async function previewPromptByProfileId(profile_id: string, asset_type?: string): Promise<PromptBuildResponse> {
  const response = await fetch(`${API_BASE_URL}/prompts/preview/${profile_id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      asset_type: asset_type || null
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to preview prompt: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function buildPrompt(request: PromptBuildRequest): Promise<PromptBuildResponse> {
  const response = await fetch(`${API_BASE_URL}/prompts/build`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to build prompt: ${response.status} - ${errorText}`);
  }

  const data = await response.json();
  
  // Ensure the response matches our interface
  return {
    success: data.success || false,
    message: data.message || '',
    system_prompt: data.system_prompt,
    user_prompt: data.user_prompt,
    context: data.context,
    metadata: data.metadata,
    warnings: data.warnings,
    custom_prompt_used: data.custom_prompt_used || false
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function validatePromptRequest(request: CustomPromptRequest): string[] {
  const errors: string[] = [];

  if (!request.project_ids || request.project_ids.length === 0) {
    errors.push('At least one project ID is required');
  }

  if (request.llm_parameters) {
    if (request.llm_parameters.temperature !== undefined && 
        (request.llm_parameters.temperature < 0 || request.llm_parameters.temperature > 2)) {
      errors.push('Temperature must be between 0 and 2');
    }

    if (request.llm_parameters.max_tokens !== undefined && 
        (request.llm_parameters.max_tokens < 100 || request.llm_parameters.max_tokens > 4000)) {
      errors.push('Max tokens must be between 100 and 4000');
    }
  }

  return errors;
}

export function formatPromptPreview(request: CustomPromptRequest): string {
  let preview = `Project IDs: ${request.project_ids.join(', ')}\n\n`;
  
  if (request.custom_system_instructions) {
    preview += `System Instructions:\n${request.custom_system_instructions}\n\n`;
  }
  
  if (request.custom_user_instructions) {
    preview += `User Instructions:\n${request.custom_user_instructions}\n\n`;
  }
  
  if (request.custom_metadata_schema) {
    preview += `Metadata Schema:\n${JSON.stringify(request.custom_metadata_schema, null, 2)}\n\n`;
  }
  
  if (request.custom_focus_areas) {
    preview += `Focus Areas:\n${JSON.stringify(request.custom_focus_areas, null, 2)}\n\n`;
  }
  
  if (request.llm_parameters) {
    preview += `LLM Parameters:\n${JSON.stringify(request.llm_parameters, null, 2)}`;
  }
  
  return preview;
}

// ============================================================================
// ENHANCED PROMPT MANAGEMENT API FUNCTIONS
// ============================================================================

export interface PromptElementsResponse {
  system_role_definition?: {
    content: string;
    is_custom: boolean;
    version?: string;
  };
  system_instructions?: {
    content: string;
    is_custom: boolean;
    version?: string;
  };
  user_instructions?: {
    content: string;
    is_custom: boolean;
    version?: string;
  };
  system_json_structure?: {
    content: string;
    is_custom: boolean;
    version?: string;
  };
  output_format?: {
    content: string;
    is_custom: boolean;
    version?: string;
  };
  focus_areas?: Record<string, {
    system_areas?: string[];
    user_areas?: string[];
    areas?: string[];
    is_custom: boolean;
  }>;
  metadata_schemas?: Record<string, {
    fields: Record<string, any>;
    is_custom: boolean;
  }>;
  project_id?: string;
  metadata?: {
    has_custom_config: boolean;
    version?: string;
  };
}

export interface UpdatePromptElementRequest {
  prompt_type: string;
  project_id: string;
  element_type: 'system_role_definition' | 'system_instructions' | 'user_instructions' | 'system_json_structure' | 'output_format';
  content: string;
}

export interface UpdateSystemFocusAreasRequest {
  prompt_type: string;
  project_id: string;
  system_focus_areas: Record<string, string[]>;
}

export interface UpdateUserFocusAreasRequest {
  prompt_type: string;
  project_id: string;
  user_focus_areas: Record<string, string[]>;
}

export interface UpdateFocusAreasRequest {
  prompt_type: string;
  project_id: string;
  focus_areas: Record<string, string[]>;
}

export interface UpdateMetadataSchemaRequest {
  prompt_type: string;
  project_id: string;
  metadata_schemas: Record<string, Record<string, any>>;
}

export interface PromptElementValidationRequest {
  prompt_type: string;
  project_id?: string;
  element_type: string;
  content: string;
}

export interface EnhancedPromptBuildRequest {
  prompt_type: string;
  project_ids: string[];
  system_role_definition?: string;
  system_instructions?: string;
  user_instructions?: string;
  system_json_structure?: string;
  output_format?: string;
  system_focus_areas?: Record<string, string[]>;
  user_focus_areas?: Record<string, string[]>;
  metadata_schemas?: Record<string, Record<string, any>>;
}

export async function getPromptElements(params: {
  prompt_type: string;
  project_id?: string;
  include_defaults?: boolean;
  include_custom?: boolean;
}): Promise<PromptElementsResponse> {
  const request = {
    prompt_type: params.prompt_type as "image" | "video",
    project_id: params.project_id,
    include_defaults: params.include_defaults ?? true,
    include_custom: params.include_custom ?? true
  };

  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/get`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get prompt elements: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function updatePromptElement(request: UpdatePromptElementRequest): Promise<{ success: boolean; version?: string }> {
  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/update-element`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update prompt element: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function updateSystemFocusAreas(request: UpdateSystemFocusAreasRequest): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/update-system-focus-areas`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update system focus areas: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function updateUserFocusAreas(request: UpdateUserFocusAreasRequest): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/update-user-focus-areas`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update user focus areas: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function updateFocusAreas(request: UpdateFocusAreasRequest): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/update-focus-areas`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update focus areas: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function updateMetadataSchema(request: UpdateMetadataSchemaRequest): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/update-metadata-schema`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update metadata schema: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function validatePromptElements(request: PromptElementValidationRequest): Promise<{
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  suggestions: string[];
}> {
  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/validate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to validate prompt elements: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function getPromptElementTemplates(): Promise<{
  success: boolean;
  templates: Record<string, any>;
}> {
  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/templates`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get prompt templates: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function buildEnhancedPrompt(request: EnhancedPromptBuildRequest): Promise<{
  success: boolean;
  prompts: Record<string, { system?: string; user?: string; error?: string }>;
}> {
  const response = await fetch(`${API_BASE_URL}/v1/prompt-elements/build`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to build enhanced prompt: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export async function clearCache(): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/prompts/cache/clear`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to clear cache: ${response.status} - ${errorText}`);
  }

  return response.json();
}

// ============================================================================
// TOKEN USAGE METRICS FUNCTIONS
// ============================================================================

export interface TokenUsageMetrics {
  prompt_version_id: number;
  project_id?: string;
  evaluation_metrics: {
    accuracy?: number;
    completeness?: number;
    relevance?: number;
    consistency?: number;
    processing_time?: number;
    token_usage?: number;
    cost?: number;
  };
  token_usage_summary: {
    total_evaluations: number;
    total_tokens: number;
    average_tokens_per_evaluation: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_api_calls: number;
    average_efficiency_score: number;
    success_rate: number;
    token_usage_breakdown: {
      prompt_tokens_percentage: number;
      completion_tokens_percentage: number;
    };
  };
}

export async function getPromptVersionTokenMetrics(
  promptVersionId: number,
  projectId?: string
): Promise<TokenUsageMetrics> {
  const params = new URLSearchParams();
  if (projectId) {
    params.append('project_id', projectId);
  }

  const response = await fetch(`${API_BASE_URL}/token-usage/prompt-version/${promptVersionId}/metrics?${params}`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get token usage metrics: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export interface TokenUsageEfficiency {
  prompt_version_id: number;
  project_id?: string;
  efficiency_metrics: {
    token_efficiency_score: number;
    success_rate: number;
    average_tokens_per_evaluation: number;
    total_api_calls: number;
    token_usage_breakdown: {
      prompt_tokens_percentage: number;
      completion_tokens_percentage: number;
    };
    total_evaluations: number;
  };
}

export async function getPromptVersionEfficiency(
  promptVersionId: number,
  projectId?: string
): Promise<TokenUsageEfficiency> {
  const params = new URLSearchParams();
  if (projectId) {
    params.append('project_id', projectId);
  }

  const response = await fetch(`${API_BASE_URL}/token-usage/prompt-version/${promptVersionId}/efficiency?${params}`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get efficiency metrics: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export interface TokenUsageCostAnalysis {
  prompt_version_id: number;
  project_id?: string;
  cost_analysis: {
    total_tokens: number;
    total_evaluations: number;
    cost_per_1k_tokens: number;
    total_cost: number;
    cost_per_evaluation: number;
    tokens_per_evaluation: number;
  };
}

export async function getPromptVersionCostAnalysis(
  promptVersionId: number,
  projectId?: string,
  costPer1kTokens: number = 0.03
): Promise<TokenUsageCostAnalysis> {
  const params = new URLSearchParams();
  if (projectId) {
    params.append('project_id', projectId);
  }
  params.append('cost_per_1k_tokens', costPer1kTokens.toString());

  const response = await fetch(`${API_BASE_URL}/token-usage/prompt-version/${promptVersionId}/cost-analysis?${params}`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get cost analysis: ${response.status} - ${errorText}`);
  }

  return response.json();
}

// ============================================================================
// UNIFIED METRICS FUNCTIONS
// ============================================================================

export interface UnifiedPromptMetrics {
  prompt_version_id: number;
  project_id?: string;
  unified_metrics: {
    overall_score: number;
    efficiency_score: number;
    quality_score: number;
    cost_effectiveness: number;
    success_rate: number;
    human_satisfaction: number;
  };
  automated_metrics: {
    accuracy?: number;
    completeness?: number;
    relevance?: number;
    consistency?: number;
    processing_time?: number;
    token_usage?: number;
    cost?: number;
  };
  token_usage_metrics: {
    total_evaluations: number;
    total_tokens: number;
    average_tokens_per_evaluation: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_api_calls: number;
    average_efficiency_score: number;
    success_rate: number;
    token_usage_breakdown: {
      prompt_tokens_percentage: number;
      completion_tokens_percentage: number;
    };
  };
  human_feedback_metrics: {
    total_evaluations: number;
    average_rating: number;
    rating_distribution: Record<string, number>;
    feedback_count: number;
    recent_feedback: Array<{
      rating: number;
      feedback?: string;
      evaluator_id: string;
      created_at: string;
    }>;
    evaluator_count: number;
    satisfaction_score: number;
  };
  cost_benefit_analysis: {
    total_cost: number;
    cost_per_evaluation: number;
    cost_effectiveness: number;
    roi_percentage: number;
    value_per_evaluation: number;
    total_value: number;
    break_even_evaluations: number;
  };
  recommendations: {
    priority: string;
    actions: Array<{
      type: string;
      priority: string;
      description: string;
      expected_improvement: string;
    }>;
    expected_improvements: {
      cost_reduction: string;
      quality_improvement: string;
      overall_score_improvement: string;
    };
    risk_level: string;
  };
}

export async function getPromptVersionUnifiedMetrics(
  promptVersionId: number,
  projectId?: string
): Promise<UnifiedPromptMetrics> {
  const params = new URLSearchParams();
  if (projectId) {
    params.append('project_id', projectId);
  }

  const response = await fetch(`${API_BASE_URL}/token-usage/prompt-version/${promptVersionId}/unified-metrics?${params}`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get unified metrics: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export interface HumanFeedbackMetrics {
  prompt_version_id: number;
  project_id?: string;
  human_feedback_metrics: {
    total_evaluations: number;
    average_rating: number;
    rating_distribution: Record<string, number>;
    feedback_count: number;
    recent_feedback: Array<{
      rating: number;
      feedback?: string;
      evaluator_id: string;
      created_at: string;
    }>;
    evaluator_count: number;
    satisfaction_score: number;
  };
}

export async function getPromptVersionHumanFeedback(
  promptVersionId: number,
  projectId?: string
): Promise<HumanFeedbackMetrics> {
  const params = new URLSearchParams();
  if (projectId) {
    params.append('project_id', projectId);
  }

  const response = await fetch(`${API_BASE_URL}/token-usage/prompt-version/${promptVersionId}/human-feedback?${params}`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get human feedback metrics: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export interface TokenUsageWithCorrelation extends TokenUsageMetrics {
  human_feedback_correlation?: {
    human_metrics: {
      total_evaluations: number;
      average_rating: number;
      rating_distribution: Record<string, number>;
      feedback_count: number;
      recent_feedback: Array<{
        rating: number;
        feedback?: string;
        evaluator_id: string;
        created_at: string;
      }>;
      evaluator_count: number;
      satisfaction_score: number;
    };
    correlation_analysis: {
      correlations: Record<string, {
        correlation: string;
        strength: string;
        insight: string;
      }>;
      overall_correlation: string;
      correlation_strength: string;
      metrics_summary: {
        avg_tokens_per_evaluation: number;
        token_efficiency_score: number;
        processing_success_rate: number;
        human_rating: number;
        satisfaction_score: number;
      };
    };
    insights: string[];
  };
}

export async function getPromptVersionTokenMetricsWithCorrelation(
  promptVersionId: number,
  projectId?: string,
  includeHumanFeedback: boolean = true
): Promise<TokenUsageWithCorrelation> {
  const params = new URLSearchParams();
  if (projectId) {
    params.append('project_id', projectId);
  }
  params.append('include_human_feedback', includeHumanFeedback.toString());

  const response = await fetch(`${API_BASE_URL}/token-usage/prompt-version/${promptVersionId}/metrics?${params}`);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get token usage metrics with correlation: ${response.status} - ${errorText}`);
  }

  return response.json();
} 

// ============================================================================
// PROMPT PROFILE MANAGEMENT FUNCTIONS
// ============================================================================

export interface PromptProfile {
  profile_id: string;
  name: string;
  prompt_type: 'image' | 'video';
  asset_type: string;
  description?: string;
  version?: string;
  is_default?: boolean;
  project_id?: string;
  custom_system_instructions?: string;
  custom_user_instructions?: string;
  custom_metadata_schema?: Record<string, any>;
  custom_system_focus_areas?: Record<string, string[]>;
  custom_user_focus_areas?: Record<string, string[]>;
  custom_focus_areas?: Record<string, any>;
  custom_output_format?: string;
  validation_rules?: Record<string, any>;
  llm_parameters?: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
    frequency_penalty?: number;
    presence_penalty?: number;
  };
  allowed_project_ids?: string[];
  created_at?: string;
  updated_at?: string;
  system_role_definition?: string;
}

export interface CreatePromptProfileRequest {
  name: string;
  prompt_type: 'image' | 'video';
  asset_type: string;
  description?: string;
  version?: string;
  system_role_definition?: string;
  custom_system_instructions?: string;
  custom_user_instructions?: string;
  custom_metadata_schema?: Record<string, any>;
  custom_system_focus_areas?: Record<string, string[]>;
  custom_user_focus_areas?: Record<string, string[]>;
  custom_focus_areas?: Record<string, any>;
  custom_output_format?: string;
  validation_rules?: Record<string, any>;
  llm_parameters?: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
    frequency_penalty?: number;
    presence_penalty?: number;
  };
  allowed_project_ids?: string[];
  project_id?: string;
}

export interface UpdatePromptProfileRequest extends CreatePromptProfileRequest {
  profile_id: string;
  version?: string;
  is_default?: boolean;
}

export async function getPromptProfiles(params?: { category?: string }): Promise<{ success: boolean; profiles: PromptProfile[] }> {
  try {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    const url = `${API_BASE_URL}/prompts/profiles${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to get prompt profiles: ${response.status} - ${await response.text()}`);
    const data = await response.json();
    // search-api returns an array of profiles
    const profiles: PromptProfile[] = (Array.isArray(data) ? data : data.profiles || []).map((profile: any) => ({
      profile_id: profile.profile_id,
      name: profile.name,
      prompt_type: profile.category || profile.prompt_type,
      asset_type: profile.metadata?.asset_type || profile.asset_type,
      description: profile.description,
      version: profile.version,
      is_default: profile.is_default,
      project_id: profile.metadata?.project_id,
      system_role_definition: profile.metadata?.system_role_definition,
      custom_system_instructions: profile.metadata?.custom_system_instructions,
      custom_user_instructions: profile.metadata?.custom_user_instructions,
      custom_metadata_schema: profile.metadata?.custom_metadata_schema,
      custom_system_focus_areas: profile.metadata?.custom_system_focus_areas,
      custom_user_focus_areas: profile.metadata?.custom_user_focus_areas,
      custom_focus_areas: profile.metadata?.custom_focus_areas,
      custom_output_format: profile.metadata?.custom_output_format,
      validation_rules: profile.metadata?.validation_rules,
      llm_parameters: profile.metadata?.llm_parameters,
      allowed_project_ids: profile.metadata?.allowed_project_ids,
      created_at: profile.created_at,
      updated_at: profile.updated_at,
    }));
    return { success: true, profiles };
  } catch (error) {
    throw error;
  }
}

export async function createPromptProfile(request: CreatePromptProfileRequest): Promise<{ success: boolean; profile: PromptProfile; message: string }> {
  try {
    // Compose backend structure
    const profileData = {
      name: request.name,
      category: request.prompt_type,
      description: request.description,
      version: request.version, // Include version in the request
      metadata: {
      asset_type: request.asset_type,
        project_id: request.project_id,
      system_role_definition: request.system_role_definition,
      custom_system_instructions: request.custom_system_instructions,
      custom_user_instructions: request.custom_user_instructions,
        custom_metadata_schema: request.custom_metadata_schema,
        custom_system_focus_areas: request.custom_system_focus_areas,
        custom_user_focus_areas: request.custom_user_focus_areas,
        custom_focus_areas: request.custom_focus_areas,
        custom_output_format: request.custom_output_format,
        validation_rules: request.validation_rules,
        llm_parameters: request.llm_parameters,
        allowed_project_ids: request.allowed_project_ids,
      },
    };
    const response = await fetch(`${API_BASE_URL}/prompts/profiles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profileData),
    });
    if (!response.ok) throw new Error(`Failed to create prompt profile: ${response.status} - ${await response.text()}`);
    const data = await response.json();
    // Fetch the created profile by id if available
    let profile: PromptProfile | undefined;
    if (data.profile_id) {
      const getResp = await fetch(`${API_BASE_URL}/prompts/profiles/${data.profile_id}`);
      if (getResp.ok) {
        const p = await getResp.json();
        profile = {
          profile_id: p.profile_id,
          name: p.name,
          prompt_type: p.category || p.prompt_type,
          asset_type: p.metadata?.asset_type || p.asset_type,
          description: p.description,
          version: p.version,
          is_default: p.is_default,
          project_id: p.metadata?.project_id,
          system_role_definition: p.metadata?.system_role_definition,
          custom_system_instructions: p.metadata?.custom_system_instructions,
          custom_user_instructions: p.metadata?.custom_user_instructions,
          custom_metadata_schema: p.metadata?.custom_metadata_schema,
          custom_system_focus_areas: p.metadata?.custom_system_focus_areas,
          custom_user_focus_areas: p.metadata?.custom_user_focus_areas,
          custom_focus_areas: p.metadata?.custom_focus_areas,
          custom_output_format: p.metadata?.custom_output_format,
          validation_rules: p.metadata?.validation_rules,
          llm_parameters: p.metadata?.llm_parameters,
          allowed_project_ids: p.metadata?.allowed_project_ids,
          created_at: p.created_at,
          updated_at: p.updated_at,
        };
      }
    }
    return { success: true, profile: profile!, message: data.message || 'Profile created successfully' };
  } catch (error) {
    throw error;
  }
}

export async function updatePromptProfile(request: UpdatePromptProfileRequest): Promise<{ success: boolean; profile: PromptProfile; message: string }> {
  if (!request.profile_id) throw new Error('profile_id is required');
  const profileData = {
      name: request.name,
    category: request.prompt_type,
    description: request.description,
    version: request.version, // Include version for manual version management
    metadata: {
      asset_type: request.asset_type,
      project_id: request.project_id,
      system_role_definition: request.system_role_definition,
      custom_system_instructions: request.custom_system_instructions,
      custom_user_instructions: request.custom_user_instructions,
      custom_metadata_schema: request.custom_metadata_schema,
      custom_system_focus_areas: request.custom_system_focus_areas,
      custom_user_focus_areas: request.custom_user_focus_areas,
      custom_focus_areas: request.custom_focus_areas,
      custom_output_format: request.custom_output_format,
      validation_rules: request.validation_rules,
      llm_parameters: request.llm_parameters,
      allowed_project_ids: request.allowed_project_ids,
    },
  };
  const response = await fetch(`${API_BASE_URL}/prompts/profiles/${request.profile_id}`, {
      method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(profileData),
  });
  if (!response.ok) throw new Error(`Failed to update prompt profile: ${response.status} - ${await response.text()}`);
  // Fetch the updated profile
  const getResp = await fetch(`${API_BASE_URL}/prompts/profiles/${request.profile_id}`);
  if (!getResp.ok) throw new Error(`Failed to fetch updated profile: ${getResp.status} - ${await getResp.text()}`);
  const p = await getResp.json();
    const profile: PromptProfile = {
    profile_id: p.profile_id,
    name: p.name,
    prompt_type: p.category || p.prompt_type,
    asset_type: p.metadata?.asset_type || p.asset_type,
    description: p.description,
    version: p.version,
    is_default: p.is_default,
    project_id: p.metadata?.project_id,
    system_role_definition: p.metadata?.system_role_definition,
    custom_system_instructions: p.metadata?.custom_system_instructions,
    custom_user_instructions: p.metadata?.custom_user_instructions,
    custom_metadata_schema: p.metadata?.custom_metadata_schema,
    custom_system_focus_areas: p.metadata?.custom_system_focus_areas,
    custom_user_focus_areas: p.metadata?.custom_user_focus_areas,
    custom_focus_areas: p.metadata?.custom_focus_areas,
    custom_output_format: p.metadata?.custom_output_format,
    validation_rules: p.metadata?.validation_rules,
    llm_parameters: p.metadata?.llm_parameters,
    allowed_project_ids: p.metadata?.allowed_project_ids,
    created_at: p.created_at,
    updated_at: p.updated_at,
  };
  return { success: true, profile, message: 'Profile updated successfully' };
}

export async function deletePromptProfile(profile_id: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/prompts/profiles/${profile_id}`, {
      method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error(`Failed to delete prompt profile: ${response.status} - ${await response.text()}`);
  return { success: true, message: 'Profile deleted successfully' };
} 